import React, { useState, useEffect } from "react";
import { Table, Spinner, Alert } from "react-bootstrap";
import api from '../services/api';

// MASTER LIST of all possible statuses. This will be used for both tables.
const ALL_POSSIBLE_STATUSES = [
    "Open",
    "Test Link Delivered",
    "Final Link Delivered",
    "Launched / Field Live",
    "Fieldwork Complete",
    "Data QC Done",
    "Data Processed / QC Done", // Catches the inconsistent status from your data
    "Interim Delivered",
    "Final Delivered",
    "Hold",
    "Closed",
];

const CummulativeSummaryTable = () => {
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allExecutives, setAllExecutives] = useState([]);

    useEffect(() => {
        const fetchSummaryData = async () => {
            try {
                setLoading(true);
                const response = await api.get("api/segments/fetch_cumulative_summary/", {
                    headers: {
                        Authorization: `Token ${localStorage.getItem("Token")}`,
                    },
                });
                const data = response.data;
                setSummaryData(data);

                // Find all unique executives for the rows
                const executives = new Set([
                    ...Object.keys(data.dp_summary || {}),
                    ...Object.keys(data.scripting_summary || {}),
                ]);
                setAllExecutives(Array.from(executives).sort());

                setError(null);
            } catch (err) {
                setError("Failed to fetch summary data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchSummaryData();
    }, []);

    if (loading) {
        return (
            <div className="text-center my-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading Summary...</p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger" className="text-center">{error}</Alert>;
    }

    if (!summaryData || allExecutives.length === 0) {
        return <Alert variant="info">No summary data available.</Alert>;
    }

    return (
        // Use a React Fragment to return multiple tables
        <>
            {/* --- SCRIPTING SUMMARY TABLE --- */}
            <h3 className="mt-4 mb-3">Scripting Summary</h3>
            <Table striped bordered hover responsive>
                <thead className="table-light">
                    <tr>
                        <th style={{ verticalAlign: 'middle' }}>Executive</th>
                        {ALL_POSSIBLE_STATUSES.map(status => (
                            <th key={`script-header-${status}`} className="text-center" style={{ minWidth: '150px' }}>{status}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {allExecutives.map(executive => (
                        <tr key={`script-${executive}`}>
                            <td>{executive}</td>
                            {ALL_POSSIBLE_STATUSES.map(status => (
                                <td key={`${executive}-script-${status}`} className="text-center">
                                    {summaryData.scripting_summary?.[executive]?.[status] || 0}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>

            {/* --- DP SUMMARY TABLE --- */}
            <h3 className="mt-5 mb-3">DP Summary</h3>
            <Table striped bordered hover responsive>
                <thead className="table-light">
                    <tr>
                        <th style={{ verticalAlign: 'middle' }}>Executive</th>
                        {ALL_POSSIBLE_STATUSES.map(status => (
                            <th key={`dp-header-${status}`} className="text-center" style={{ minWidth: '150px' }}>{status}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {allExecutives.map(executive => (
                        <tr key={`dp-${executive}`}>
                            <td>{executive}</td>
                            {ALL_POSSIBLE_STATUSES.map(status => (
                                <td key={`${executive}-dp-${status}`} className="text-center">
                                    {summaryData.dp_summary?.[executive]?.[status] || 0}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    );
};

export default CummulativeSummaryTable;